import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { NormativaLink as DecretoLink } from "@/components/shared/NormativaLink";
import { cn } from "@/lib/utils";
import type { FiscalBenefitMotivo } from "@/services/fiscalService";

const DECRETO_URL =
  "https://www.impo.com.uy/bases/decretos/206-2019?tipoServicio=11";
const RESOLUCION_URL =
  "https://www.impo.com.uy/bases/resoluciones-dgi/3738-2019?tipoServicio=11";
const DGI_BENEFIT_URL =
  "https://www.gub.uy/direccion-general-impositiva/comunicacion/publicaciones/credito-contratacion-servicios-soluciones-facturacion-electronica-ui-80";

/**
 * DGI's three conditions, verbatim in intent. The order matters: `iva_minimo`
 * is the Art.5 flow where Fiscalo discounts the credit off the price, and the
 * other two are Art.4 where the client offsets it on their own return. The
 * copy deliberately says which is which, because the client's cash outcome
 * differs and they should know before signing.
 */
const MOTIVO_OPTIONS: {
  value: FiscalBenefitMotivo;
  labelKey: string;
  fallback: string;
  labelKeyAcct: string;
  fallbackAcct: string;
}[] = [
  {
    value: "ingresos_bajo_750k",
    labelKey: "fiscalBenefit.motivo.ingresosBajo750k",
    fallback:
      "Mis ingresos del ejercicio anterior no superaron el equivalente a UI 750.000.",
    labelKeyAcct: "fiscalBenefit.motivo.ingresosBajo750kAcct",
    fallbackAcct:
      "Los ingresos del cliente en el ejercicio anterior no superaron el equivalente a UI 750.000.",
  },
  {
    value: "inicio_actividades",
    labelKey: "fiscalBenefit.motivo.inicioActividades",
    fallback: "Inicié actividades en el ejercicio declarado.",
    labelKeyAcct: "fiscalBenefit.motivo.inicioActividadesAcct",
    fallbackAcct: "El cliente inició actividades en el ejercicio declarado.",
  },
  {
    value: "iva_minimo",
    labelKey: "fiscalBenefit.motivo.ivaMinimo",
    fallback: "Soy contribuyente del régimen de IVA mínimo.",
    labelKeyAcct: "fiscalBenefit.motivo.ivaMinimoAcct",
    fallbackAcct: "El cliente es contribuyente del régimen de IVA mínimo.",
  },
];

export interface FiscalBenefitDeclarationValue {
  complies: boolean | null;
  motivo: FiscalBenefitMotivo | null;
}

export interface FiscalBenefitDeclarationProps {
  value: FiscalBenefitDeclarationValue;
  onChange: (next: FiscalBenefitDeclarationValue) => void;
  /** 'accountant' swaps second-person copy for third-person about the client. */
  audience?: "owner" | "accountant";
  idPrefix?: string;
  disabled?: boolean;
}

/**
 * Collects the client's declaration that they meet the Decreto 206/019
 * prerequisites for the 80 UI e-factura credit.
 *
 * This is an attestation, not a preference. Fiscalo is the Proveedor Habilitado
 * that computes and recovers the Art.5 credit on its own Form 2181 filing, so
 * the client's declaration is the evidence behind our filing — which is why the
 * decree and resolution are linked inline rather than summarised, and why "no
 * cumplo" is an explicit choice rather than the absence of a tick.
 */
export function FiscalBenefitDeclaration({
  value,
  onChange,
  audience = "owner",
  idPrefix = "fiscal-benefit",
  disabled = false,
}: FiscalBenefitDeclarationProps): JSX.Element {
  const { t } = useTranslation();
  const isAccountant = audience === "accountant";
  const [touched, setTouched] = useState(false);

  const choose = (complies: boolean): void => {
    setTouched(true);
    // Switching to "no cumplo" drops any motivo: a negative declaration must not
    // retain a reason that could later be read back as eligibility.
    onChange({ complies, motivo: complies ? value.motivo : null });
  };

  const showMotivoError = touched && value.complies === true && !value.motivo;

  return (
    <div className="space-y-4">
      <div className="space-y-2 text-sm text-muted-foreground">
        <p>
          {isAccountant
            ? t(
                "fiscalBenefit.introAcct",
                "DGI otorga un crédito fiscal de 80 UI por mes a quienes contratan facturación electrónica. Necesitamos que declares si tu cliente cumple los requisitos.",
              )
            : t(
                "fiscalBenefit.intro",
                "DGI otorga un crédito fiscal de 80 UI por mes a quienes contratan facturación electrónica. Necesitamos que nos indiques si cumplís los requisitos.",
              )}
        </p>
        <p className="text-xs">
          {t("fiscalBenefit.sources", "Normativa:")}{" "}
          <DecretoLink href={DECRETO_URL}>Decreto N° 206/019</DecretoLink>
          {" · "}
          <DecretoLink href={RESOLUCION_URL}>
            Resolución DGI N° 3738/019
          </DecretoLink>
          {" · "}
          <DecretoLink href={DGI_BENEFIT_URL}>
            {t("fiscalBenefit.dgiPage", "Información de DGI")}
          </DecretoLink>
        </p>
      </div>

      <fieldset className="space-y-2" disabled={disabled}>
        <legend className="sr-only">
          {t("fiscalBenefit.legend", "Declaración de beneficio fiscal")}
        </legend>

        {/* value.complies is a tri-state: true / false / null for "not yet
            declared". RadioGroup is string-valued, so it is encoded here and
            decoded in onValueChange — null must stay unselected, because a
            declaration nobody made must never render as "no". */}
        <RadioGroup
          value={
            value.complies === true ? "yes" : value.complies === false ? "no" : undefined
          }
          onValueChange={(v) => choose(v === "yes")}
          className="space-y-2"
        >
        {[
          {
            complies: false,
            id: `${idPrefix}-no`,
            label: t(
              "fiscalBenefit.optionNo",
              "No cumplo los requisitos, no accedo al beneficio",
            ),
            labelAcct: t(
              "fiscalBenefit.optionNoAcct",
              "No cumple los requisitos, no accede al beneficio",
            ),
          },
          {
            complies: true,
            id: `${idPrefix}-yes`,
            label: t("fiscalBenefit.optionYes", "Declaro cumplir los requisitos"),
            labelAcct: t(
              "fiscalBenefit.optionYesAcct",
              "Declaro que mi cliente cumple los requisitos",
            ),
          },
        ].map((opt) => (
          <Label
            key={opt.id}
            htmlFor={opt.id}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
              value.complies === opt.complies
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted/50",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <RadioGroupItem
              id={opt.id}
              value={opt.complies ? "yes" : "no"}
              disabled={disabled}
              className="mt-1 shrink-0"
            />
            <span className="text-sm text-foreground">
              {isAccountant ? opt.labelAcct : opt.label}
            </span>
          </Label>
        ))}
        </RadioGroup>
      </fieldset>

      {value.complies === true && (
        <div className="space-y-2 rounded-lg border border-border p-3">
          <Label className="text-foreground">
            {t("fiscalBenefit.motivoTitle", "Motivo del beneficio")}
          </Label>
          <p className="text-xs text-muted-foreground">
            {isAccountant
              ? t(
                  "fiscalBenefit.motivoHelpAcct",
                  "Elegí la condición por la que el cliente accede al beneficio. Determina cómo se aplica el crédito.",
                )
              : t(
                  "fiscalBenefit.motivoHelp",
                  "Elegí la condición por la que accedés al beneficio. Determina cómo se aplica el crédito.",
                )}
          </p>
          <fieldset className="space-y-2 pt-1" disabled={disabled}>
            <legend className="sr-only">
              {t("fiscalBenefit.motivoTitle", "Motivo del beneficio")}
            </legend>
            {/* motivo is already a string union, so it needs no encoding —
                but it is optional, and undefined must render as nothing
                selected rather than as the first option. */}
            <RadioGroup
              value={value.motivo ?? undefined}
              onValueChange={(v) => {
                setTouched(true);
                onChange({ complies: true, motivo: v as FiscalBenefitMotivo });
              }}
              className="space-y-2"
            >
              {MOTIVO_OPTIONS.map((opt) => (
                <Label
                  key={opt.value}
                  htmlFor={`${idPrefix}-motivo-${opt.value}`}
                  className="flex cursor-pointer items-start gap-3 text-sm font-normal text-foreground"
                >
                  <RadioGroupItem
                    id={`${idPrefix}-motivo-${opt.value}`}
                    value={opt.value}
                    disabled={disabled}
                    className="mt-1 shrink-0"
                  />
                  <span>
                    {isAccountant
                      ? t(opt.labelKeyAcct, opt.fallbackAcct)
                      : t(opt.labelKey, opt.fallback)}
                  </span>
                </Label>
              ))}
            </RadioGroup>
          </fieldset>
          {value.motivo === "iva_minimo" && (
            <p className="text-xs text-muted-foreground">
              {isAccountant
                ? t(
                    "fiscalBenefit.motivoIvaMinimoNoteAcct",
                    "En este caso Fiscalo descuenta el crédito directamente del precio del servicio del cliente.",
                  )
                : t(
                    "fiscalBenefit.motivoIvaMinimoNote",
                    "En este caso el crédito lo descuenta Fiscalo directamente del precio del servicio.",
                  )}
            </p>
          )}
          {(value.motivo === "inicio_actividades" ||
            value.motivo === "ingresos_bajo_750k") && (
            <p className="text-xs text-muted-foreground">
              {isAccountant
                ? t(
                    "fiscalBenefit.motivoGeneralNoteAcct",
                    "En este caso el crédito lo computa el cliente en su propia declaración ante DGI; el precio del servicio no cambia.",
                  )
                : t(
                    "fiscalBenefit.motivoGeneralNote",
                    "En este caso el crédito se computa en tu propia declaración ante DGI; el precio del servicio no cambia.",
                  )}
            </p>
          )}
          {showMotivoError && (
            <p role="alert" className="text-xs text-destructive">
              {t(
                "fiscalBenefit.motivoRequired",
                "Elegí un motivo para completar la declaración.",
              )}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** True when the value is complete enough to submit. */
export function isFiscalBenefitDeclarationComplete(
  value: FiscalBenefitDeclarationValue,
): boolean {
  if (value.complies === null) return false;
  return value.complies ? value.motivo !== null : true;
}
